import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAuth } from "@perlab/auth-bridge";
import { Button, RightInspectorSection } from "@perlab/ui";
import { FileText } from "lucide-react";
import { authAccessIdentity } from "../api/authAccessIdentity";
import { useScopeData, type ScopeData } from "../state/useScopeData";
import { ConflictError, ForbiddenError, ScopeSealedOrMissingError, SessionExpiredError, ValidationError } from "../state/denialErrors";
import { PROJECT_CONTEXT_LIMITS, projectContextClient, validateProjectContextDraft, type ProjectContext } from "./projectContextApi";
import "./projectContext.css";

export function ProjectContextModule(): React.ReactElement | null {
  const scope = useScopeData();
  const { user } = useAuth();
  const identity = authAccessIdentity(user);
  if (!scope.ready || scope.level !== "project" || !scope.readerScopeNodeId || !identity) return null;
  return <ProjectContextForm key={`${scope.readerScopeNodeId}:${identity}`} scope={scope} scopeId={scope.readerScopeNodeId} />;
}
function ProjectContextForm({ scope, scopeId }: { scope: ScopeData; scopeId: string }): React.ReactElement {
  const client = useMemo(() => projectContextClient(scope.fetchScoped, scopeId), [scope.fetchScoped, scopeId]);
  const [snapshot, setSnapshot] = useState<ProjectContext | null>(null);
  const [products, setProducts] = useState("");
  const [objective, setObjective] = useState("");
  const [editing, setEditing] = useState(false);
  const [operation, setOperation] = useState<"read" | "save" | null>("read");
  const busy = operation !== null;
  const [reviewLatest, setReviewLatest] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const editButton = useRef<HTMLButtonElement>(null);
  const productInput = useRef<HTMLTextAreaElement>(null);
  const restoreFocus = useRef(false);
  const id = useId();
  const draft = { products: products.split(/\r?\n/).map(value => value.trim()).filter(Boolean), objective: objective.trim() };
  const valid = validateProjectContextDraft(draft);
  const stop = useCallback(() => { generation.current++; controller.current?.abort(); }, []);
  const deny = (failure: unknown): boolean => failure instanceof ForbiddenError || failure instanceof ScopeSealedOrMissingError || failure instanceof SessionExpiredError;
  const load = useCallback(async (preserveDraft = false) => {
    stop();
    const current = generation.current, request = new AbortController(); controller.current = request;
    setOperation("read"); setError(null); setNotice(null);
    try {
      const next = await client.read(request.signal);
      if (request.signal.aborted || generation.current !== current) return;
      setSnapshot(next); setNeedsReload(false); setReviewLatest(preserveDraft && next.can_edit);
      if (!preserveDraft || !next.can_edit) {
        setProducts(next.products.join("\n")); setObjective(next.objective); setEditing(false);
      } else setNotice("Latest saved context loaded. Your draft is kept below; review it before saving.");
    } catch (failure) {
      if (request.signal.aborted || generation.current !== current) return;
      setSnapshot(null); setNeedsReload(true);
      if (deny(failure)) { setEditing(false); setProducts(""); setObjective(""); }
      setError(deny(failure) ? "Project context is unavailable for this project or sign-in." : "Project context could not be loaded. Reload to check again.");
    } finally {
      if (!request.signal.aborted && generation.current === current) setOperation(null);
    }
  }, [client, stop]);
  useEffect(() => { void load(); return stop; }, [load, stop]);
  useEffect(() => { if (editing) productInput.current?.focus({ preventScroll: true }); }, [editing]);
  useEffect(() => {
    if (!editing && !busy && restoreFocus.current) { editButton.current?.focus({ preventScroll: true }); restoreFocus.current = false; }
  }, [editing, busy]);

  async function save(): Promise<void> {
    if (busy || !snapshot?.can_edit || needsReload || !valid) return;
    stop();
    const current = generation.current, request = new AbortController(); controller.current = request;
    setOperation("save"); setError(null); setNotice(null);
    try {
      const next = await client.save(snapshot.revision, draft, request.signal);
      if (request.signal.aborted || generation.current !== current) return;
      setSnapshot(next); setProducts(next.products.join("\n")); setObjective(next.objective); setEditing(false);
      setNotice("Project context saved. No analysis was started.");
      setReviewLatest(false); restoreFocus.current = true;
    } catch (failure) {
      if (request.signal.aborted || generation.current !== current) return;
      if (deny(failure)) {
        setSnapshot(null); setEditing(false); setProducts(""); setObjective(""); setNeedsReload(true);
        setError("Project context is unavailable for this project or sign-in.");
      } else if (failure instanceof ValidationError) {
        setError("Check the products and objective before saving.");
      } else {
        setNeedsReload(true);
        setError(failure instanceof ConflictError
          ? "Project context changed since you opened it. Reload the latest version, review your draft, then save."
          : "The save could not be confirmed. Reload the latest context before saving again; your draft is kept here.");
      }
    } finally {
      if (!request.signal.aborted && generation.current === current) setOperation(null);
    }
  }
  function cancel(): void {
    if (busy || !snapshot) return;
    setProducts(snapshot.products.join("\n")); setObjective(snapshot.objective); setEditing(false);
    setNotice(null); setReviewLatest(false); restoreFocus.current = true;
    if (!needsReload) setError(null);
  }
  return <RightInspectorSection id="project-context" title="Project context" icon={<FileText size={14} aria-hidden />}
    action={snapshot?.can_edit && !editing ? <Button ref={editButton} size="sm" variant="secondary" aria-label="Edit project context"
      disabled={busy || needsReload} onClick={() => { setEditing(true); setNotice(null); }}>Edit</Button> : undefined}>
    <div className="project-context">
      <p>Describe the project to find relevant PERLab experience.</p>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {busy && <p role="status">{operation === "save" ? "Saving project context..." : "Loading project context..."}</p>}
      {snapshot && <>
        {editing ? <form onSubmit={event => { event.preventDefault(); void save(); }}>
          <fieldset disabled={busy}>
            <label htmlFor={`${id}-products`}>Products</label>
            <textarea ref={productInput} id={`${id}-products`} value={products} rows={3}
              onChange={event => setProducts(event.target.value)} aria-describedby={`${id}-products-help`} />
            <small id={`${id}-products-help`}>One per line; up to 12 products, 120 characters each. Optional.</small>
            <label htmlFor={`${id}-objective`}>Objective</label>
            <textarea id={`${id}-objective`} value={objective} rows={5} maxLength={PROJECT_CONTEXT_LIMITS.objectiveLength}
              onChange={event => setObjective(event.target.value)} aria-describedby={`${id}-objective-help`} />
            <small id={`${id}-objective-help`}>{objective.length} / {PROJECT_CONTEXT_LIMITS.objectiveLength} characters. Optional.</small>
            {!valid && <p role="alert">Use at most 12 distinct products, each up to 120 characters, and an objective up to 2,000 characters.</p>}
            {reviewLatest && <details open><summary>Latest saved context · revision {snapshot.revision}</summary>
              <p>{snapshot.products.join(", ") || "No products"}</p><p>{snapshot.objective || "No objective"}</p></details>}
            <div className="project-context__actions"><Button type="submit" size="sm" disabled={!valid || needsReload}>Save context</Button>
              <Button type="button" size="sm" variant="secondary" onClick={cancel}>Cancel</Button></div>
          </fieldset>
        </form> : <dl className="project-context__summary">
          <div><dt>Project name</dt><dd>{snapshot.project_name}</dd></div>
          <div><dt>Project objective</dt><dd>{snapshot.objective || "Not provided"}</dd></div>
          <div><dt>Product details</dt><dd>
            {snapshot.products.length ? <ul>{snapshot.products.map(value => <li key={value}>{value}</li>)}</ul> : "Not provided"}
            <dl className="project-context__product-fields">
              {["Product Category", "Product Line", "Item", "Commodity"].map(label =>
                <div key={label}><dt>{label}</dt><dd className="project-context__unavailable">&mdash;</dd></div>)}
            </dl>
          </dd></div>
          {["Industry", "Relevant towers", "Improvement levers"].map(label =>
            <div key={label}><dt>{label}</dt><dd className="project-context__unavailable">&mdash;</dd></div>)}
        </dl>}
        {!snapshot.can_edit && <p>You have read-only access to this project context.</p>}
        <small className="project-context__connection-note">Products and objective are saved here. The fields from Project Setup are not connected yet.</small>
        <details className="project-context__consumers"><summary>Connected tools</summary>
          <ul>{([['client_intelligence', 'Client Intelligence'], ['perlab', 'PERLab IP'], ['kearney', 'Kearney IP']] as const).map(([key, label]) =>
            <li key={key}><span>{label}</span><span>{snapshot.consumers[key] === "available" ? "Context adapter available" : "Not connected"}</span></li>)}</ul>
          <p>Saving context does not run analysis.</p>
        </details>
      </>}
      {needsReload && <Button size="sm" variant="secondary" disabled={busy} onClick={() => { void load(editing); }}>Reload latest context</Button>}
    </div>
  </RightInspectorSection>;
}
